import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { billingService, BillingInfo } from '../services/billing';

interface AccountProps {
  userId: number;
  user: User;
  onNavigateBack: () => void;
}

const Account: React.FC<AccountProps> = ({ userId, user, onNavigateBack }) => {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ message: string; cancelAtPeriodEnd: boolean; currentPeriodEnd: number } | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchBillingInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const info = await billingService.getBillingInfo(userId);
      setBillingInfo(info);
    } catch (err) {
      console.error('Error fetching billing info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchBillingInfo();
  }, [fetchBillingInfo]);

  const handleCancelSubscription = async () => {
    try {
      setIsCanceling(true);
      const result = await billingService.cancelSubscription(userId, cancellationReason);
      setCancelResult(result);
      setShowCancelConfirm(false);
      setCancellationReason('');
      
      // Refresh billing info to show updated status
      const updatedInfo = await billingService.getBillingInfo(userId);
      setBillingInfo(updatedInfo);
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      const response = await fetch('/api/account/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete account');
      }

      // Account deleted successfully - redirect to logout/home
      window.location.href = '/';
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionStatusBadge = (status: string, cancelAtPeriodEnd?: boolean) => {
    const statusColors = {
      'trial': 'bg-blue-100 text-blue-800',
      'active': 'bg-green-100 text-green-800',
      'cancelled': 'bg-yellow-100 text-yellow-800',
      'canceling': 'bg-yellow-100 text-yellow-800',
      'expired': 'bg-red-100 text-red-800',
      'past_due': 'bg-orange-100 text-orange-800'
    };

    // If subscription is active but set to cancel at period end, show as "Canceling"
    const displayStatus = (status === 'active' && cancelAtPeriodEnd) ? 'canceling' : status;
    const displayText = displayStatus === 'canceling' ? 'Canceling' : 
                       displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1).replace('_', ' ');

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[displayStatus as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
        {displayText}
      </span>
    );
  };

  const getPlanName = (priceId?: string) => {
    // You can map Stripe price IDs to plan names here
    if (!priceId) return 'Standard Plan';
    
    // Example mapping - adjust based on your actual Stripe price IDs
    const planNames = {
      'price_standard_monthly': 'Standard Plan (Monthly)',
      'price_standard_yearly': 'Standard Plan (Yearly)',
      // Add more mappings as needed
    };
    
    return planNames[priceId as keyof typeof planNames] || 'Standard Plan';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/8"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onNavigateBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Account Management</h1>
          <p className="mt-2 text-gray-600">Manage your subscription and account settings</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {cancelResult && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Subscription Updated</h3>
                <div className="mt-2 text-sm text-green-700">
                  {cancelResult.message}
                  {cancelResult.cancelAtPeriodEnd && (
                    <div className="mt-1">
                      Your subscription will remain active until {formatDate(cancelResult.currentPeriodEnd)}.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{user.name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Status</label>
                  <div className="mt-1">
                    {billingInfo ? getSubscriptionStatusBadge(billingInfo.subscriptionStatus, billingInfo.subscription?.cancelAtPeriodEnd) : getSubscriptionStatusBadge(user.subscriptionStatus)}
                  </div>
                </div>
                {user.createdAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Member Since</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Delete Account Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Danger Zone</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Once you delete your account, there is no going back. This will permanently delete all your data.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200 w-full"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Subscription Details</h2>
              
              {billingInfo ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Plan</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {billingInfo.subscription?.priceId ? getPlanName(billingInfo.subscription.priceId) : 'Standard Plan'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">
                        {getSubscriptionStatusBadge(billingInfo.subscriptionStatus, billingInfo.subscription?.cancelAtPeriodEnd)}
                      </div>
                    </div>
                  </div>

                  {billingInfo.subscriptionStatus === 'trial' && billingInfo.trialDaysRemaining > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">Trial Period</h3>
                          <div className="mt-2 text-sm text-blue-700">
                            You have {billingInfo.trialDaysRemaining} days remaining in your free trial.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {billingInfo.subscription && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Subscription Price</label>
                          <p className="mt-1 text-sm text-gray-900 font-semibold">
                            {billingInfo.subscription.amount && billingInfo.subscription.currency ? 
                              `${billingInfo.subscription.currency.toUpperCase()} £${(billingInfo.subscription.amount / 100).toFixed(2)}/${billingInfo.subscription.interval || 'month'}` : 
                              '£12.50/month'
                            }
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Exc VAT (VAT added at checkout)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Auto-renewal</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {billingInfo.subscription.cancelAtPeriodEnd ? 'Disabled (Canceling)' : 'Enabled'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        {billingInfo.subscription.lastPayment && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Last Payment</label>
                            <p className="mt-1 text-sm text-gray-900">
                              £{(billingInfo.subscription.lastPayment.amount / 100).toFixed(2)} on {formatDate(billingInfo.subscription.lastPayment.date)}
                            </p>
                            {billingInfo.subscription.lastPayment.invoiceUrl && (
                              <a 
                                href={billingInfo.subscription.lastPayment.invoiceUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                              >
                                View Invoice →
                              </a>
                            )}
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Next Billing Date</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {billingInfo.subscription?.currentPeriodEnd ? formatDate(billingInfo.subscription.currentPeriodEnd) : 'Not available'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Subscription Actions */}
                  {billingInfo.subscription && billingInfo.subscription.status === 'active' && !billingInfo.subscription.cancelAtPeriodEnd && (
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-md font-medium text-gray-900 mb-3">Subscription Actions</h3>
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                      >
                        Cancel Subscription
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        You'll retain access until the end of your current billing period.
                      </p>
                    </div>
                  )}

                  {/* Trial Actions */}
                  {billingInfo.subscriptionStatus === 'trial' && billingInfo.trialDaysRemaining > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-md font-medium text-gray-900 mb-3">Upgrade to Premium</h3>
                      <button
                        onClick={() => billingService.redirectToCheckout(userId)}
                        className="bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                      >
                        Upgrade Now
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        Continue using all features after your trial ends.
                      </p>
                    </div>
                  )}

                  {/* Cancelled/Expired Subscription Actions */}
                  {(billingInfo.subscriptionStatus === 'cancelled' || billingInfo.subscriptionStatus === 'expired' || 
                    (billingInfo.subscriptionStatus === 'trial' && billingInfo.trialDaysRemaining <= 0)) && (
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-md font-medium text-gray-900 mb-3">Reactivate Your Subscription</h3>
                      <button
                        onClick={() => billingService.redirectToCheckout(userId)}
                        className="bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                      >
                        Upgrade Now
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        Get instant access to all Search Console data and SEO insights.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Unable to load billing information</h3>
                  <p className="mt-1 text-sm text-gray-500">There was a problem loading your subscription details.</p>
                  <div className="mt-4">
                    <button
                      onClick={fetchBillingInfo}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                      <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Try Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Need Help?</h2>
          <div className="prose prose-sm text-gray-600">
            <p>If you have questions about your subscription or need to update your billing information, please contact our support team on <a href="mailto:hello@serprocket.ai" className="text-orange-600 hover:text-orange-700">hello@serprocket.ai</a></p>
            <p>If you wish to cancel your account subscription payments, you can do so above.</p>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-2">Cancel Subscription</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
                </p>
                <div className="text-left">
                  <label htmlFor="cancellationReason" className="block text-sm font-medium text-gray-700 mb-2">
                    Help us improve - Why are you canceling? (optional)
                  </label>
                  <textarea
                    id="cancellationReason"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="e.g., Found a better solution, Too expensive, Not using it enough..."
                  />
                </div>
              </div>
              <div className="items-center px-4 py-3 flex justify-center space-x-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isCanceling}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-2">Delete Account</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-red-600 font-semibold mb-2">
                  This action cannot be undone!
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  This will permanently delete your account and all associated data including:
                </p>
                <ul className="text-xs text-gray-600 text-left mb-4 space-y-1">
                  <li>• Your profile information</li>
                  <li>• All tracked keywords</li>
                  <li>• Property connections</li>
                  <li>• Search Console data</li>
                </ul>
                {billingInfo?.subscription && billingInfo.subscription.status === 'active' && !billingInfo.subscription.cancelAtPeriodEnd && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Note:</strong> You have an active subscription. We will automatically cancel it before deleting your account.
                    </p>
                  </div>
                )}
              </div>
              <div className="items-center px-4 py-3 flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;